import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBulletins, createBulletin, acknowledgeBulletin } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, 
  Plus, 
  Check, 
  RefreshCw,
  Filter,
  Bell,
  Shield,
  Info,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BulletinsPage = () => {
  const { user, isSupervisor } = useAuth();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [newBulletin, setNewBulletin] = useState({
    title: '',
    content: '',
    bulletin_type: 'safety',
    priority: 'normal',
    requires_acknowledgement: true
  });

  useEffect(() => {
    fetchBulletins();
  }, [typeFilter]);

  const fetchBulletins = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.bulletin_type = typeFilter;
      const response = await getBulletins(params);
      setBulletins(response.data);
    } catch (error) {
      console.error('Failed to fetch bulletins:', error);
      toast.error('Failed to load bulletins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBulletin = async (e) => {
    e.preventDefault();
    if (!newBulletin.title || !newBulletin.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await createBulletin({
        ...newBulletin,
        work_order_ids: [],
        project_types: [],
        asset_types: [],
        location_names: []
      });
      toast.success('Bulletin created successfully');
      setShowCreateDialog(false);
      setNewBulletin({
        title: '',
        content: '',
        bulletin_type: 'safety',
        priority: 'normal',
        requires_acknowledgement: true
      });
      fetchBulletins();
    } catch (error) {
      toast.error('Failed to create bulletin');
    }
  };

  const handleAcknowledge = async (bulletinId) => {
    try {
      await acknowledgeBulletin(bulletinId);
      toast.success('Bulletin acknowledged');
      fetchBulletins();
    } catch (error) {
      toast.error('Failed to acknowledge bulletin');
    }
  };

  const getBulletinIcon = (type) => {
    switch (type) {
      case 'safety': return <Shield className="w-5 h-5 text-red-500" />;
      case 'outage': return <AlertCircle className="w-5 h-5 text-amber-500" />;
      case 'procedure': return <Info className="w-5 h-5 text-blue-500" />;
      case 'vendor': return <Bell className="w-5 h-5 text-purple-500" />;
      case 'management': return <AlertTriangle className="w-5 h-5 text-slate-500" />;
      default: return <AlertTriangle className="w-5 h-5 text-slate-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'normal': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-slate-100 text-slate-800 border-slate-300';
      default: return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const getBulletinBorderColor = (type) => {
    switch (type) {
      case 'safety': return 'border-l-red-500';
      case 'outage': return 'border-l-amber-500';
      case 'procedure': return 'border-l-blue-500';
      case 'vendor': return 'border-l-purple-500';
      case 'management': return 'border-l-slate-500';
      default: return 'border-l-slate-500';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="bulletins-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Alerts & Bulletins
          </h1>
          <p className="text-slate-600">Safety notices, outage alerts, and important updates</p>
        </div>
        {isSupervisor() && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#F97316] hover:bg-[#EA580C]" data-testid="create-bulletin-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Bulletin
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 border-green-200">
              <DialogHeader>
                <DialogTitle className="text-green-800">Create New Bulletin</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateBulletin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-green-800">Title *</Label>
                  <Input
                    placeholder="Bulletin title"
                    value={newBulletin.title}
                    onChange={(e) => setNewBulletin({ ...newBulletin, title: e.target.value })}
                    required
                    className="bg-white/80 border-green-200 focus:border-green-400"
                    data-testid="bulletin-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-green-800">Type</Label>
                    <Select 
                      value={newBulletin.bulletin_type} 
                      onValueChange={(v) => setNewBulletin({ ...newBulletin, bulletin_type: v })}
                    >
                      <SelectTrigger className="bg-white/80 border-green-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="safety">Safety</SelectItem>
                        <SelectItem value="outage">Outage</SelectItem>
                        <SelectItem value="procedure">Procedure</SelectItem>
                        <SelectItem value="vendor">Vendor</SelectItem>
                        <SelectItem value="management">Management</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-green-800">Priority</Label>
                    <Select 
                      value={newBulletin.priority} 
                      onValueChange={(v) => setNewBulletin({ ...newBulletin, priority: v })}
                    >
                      <SelectTrigger className="bg-white/80 border-green-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-green-800">Content *</Label>
                  <Textarea
                    placeholder="Bulletin content..."
                    value={newBulletin.content}
                    onChange={(e) => setNewBulletin({ ...newBulletin, content: e.target.value })}
                    rows={4}
                    required
                    className="bg-white/80 border-green-200 focus:border-green-400"
                    data-testid="bulletin-content-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requires_ack"
                    checked={newBulletin.requires_acknowledgement}
                    onChange={(e) => setNewBulletin({ ...newBulletin, requires_acknowledgement: e.target.checked })}
                    className="rounded border-green-300"
                  />
                  <Label htmlFor="requires_ack" className="text-green-800">Requires acknowledgement</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-green-300 hover:bg-green-100">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="submit-bulletin-btn">
                    Create Bulletin
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Tabs value={typeFilter} onValueChange={setTypeFilter}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="safety">Safety</TabsTrigger>
                <TabsTrigger value="outage">Outage</TabsTrigger>
                <TabsTrigger value="procedure">Procedure</TabsTrigger>
                <TabsTrigger value="vendor">Vendor</TabsTrigger>
                <TabsTrigger value="management">Management</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Bulletins List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#0056D2]" />
        </div>
      ) : bulletins.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold mb-2">No Bulletins Found</h3>
            <p className="text-slate-500">
              {typeFilter !== 'all' 
                ? `No ${typeFilter} bulletins at this time` 
                : 'No bulletins have been posted yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {bulletins.map((bulletin) => {
            const isAcknowledged = bulletin.acknowledgements?.some(
              (ack) => ack.user_id === user?.id
            );
            const ackCount = bulletin.acknowledgements?.length || 0;
            
            return (
              <Card 
                key={bulletin.id}
                className={`border-l-4 ${getBulletinBorderColor(bulletin.bulletin_type)}`}
                data-testid={`bulletin-card-${bulletin.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {getBulletinIcon(bulletin.bulletin_type)}
                        <Badge className={getPriorityColor(bulletin.priority)}>
                          {bulletin.priority}
                        </Badge>
                        <Badge variant="outline">{bulletin.bulletin_type}</Badge>
                        {isAcknowledged && (
                          <Badge className="bg-green-100 text-green-800">
                            <Check className="w-3 h-3 mr-1" />
                            Acknowledged
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-xl font-bold mb-2">{bulletin.title}</h3>
                      <p className="text-slate-700 whitespace-pre-wrap">{bulletin.content}</p>
                      <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                        <span>Posted by {bulletin.created_by_name}</span>
                        <span>•</span>
                        <span>{new Date(bulletin.created_at).toLocaleDateString()}</span>
                        {bulletin.requires_acknowledgement && (
                          <>
                            <span>•</span>
                            <span>{ackCount} acknowledgements</span>
                          </>
                        )}
                      </div>
                    </div>
                    {bulletin.requires_acknowledgement && !isAcknowledged && (
                      <Button 
                        onClick={() => handleAcknowledge(bulletin.id)}
                        className="bg-[#0056D2] hover:bg-[#0044A4]"
                        data-testid={`ack-btn-${bulletin.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BulletinsPage;
